/**
 * Shared transformation utilities for inspection report data.
 * Used by both PreviewReportPage (admin) and CustomerReportPage (customer portal).
 */

/**
 * Transform Q&A categories from live-progress into report format
 */
export function transformCategoriesToReportFormat(categories, categoryRatings = {}, aiInsights = {}) {
  if (!categories || !Array.isArray(categories) || categories.length === 0) {
    return [];
  }

  // AI rating lookup — same logic as Q&A tab in LiveProgressModal
  const getAICategoryRating = (catName, catKey) => {
    const aiCategoryRatings = aiInsights.category_ratings || {};
    if (aiCategoryRatings[catName]) { const r = aiCategoryRatings[catName]; return typeof r === 'object' ? r.rating : r; }
    if (aiCategoryRatings[catKey]) { const r = aiCategoryRatings[catKey]; return typeof r === 'object' ? r.rating : r; }
    const normalizeKey = (str) => (str?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_') || '').replace(/^_+|_+$/g, '');
    const normalizedCatKey = normalizeKey(catName);
    for (const [key, value] of Object.entries(aiCategoryRatings)) {
      if (normalizeKey(key) === normalizedCatKey || key.toLowerCase() === catName?.toLowerCase()) return typeof value === 'object' ? value.rating : value;
    }
    const aiConditions = aiInsights.condition_ratings || {};
    const name = catName?.toLowerCase() || '';
    const condMap = (cond) => { const c = cond?.toUpperCase(); return c === 'BAD' ? 2 : c === 'AVERAGE' ? 6 : c === 'GOOD' ? 8 : c === 'VERY GOOD' ? 9 : c === 'POOR' ? 2 : (c === 'FAIR') ? 5 : c === 'EXCELLENT' ? 9 : null; };
    if (name.includes('engine') || name.includes('health')) return condMap(aiConditions.engine);
    if (name.includes('exterior')) return condMap(aiConditions.exterior);
    if (name.includes('interior')) return condMap(aiConditions.interior);
    if (name.includes('transmission')) return condMap(aiConditions.transmission);
    return null;
  };

  return categories.map((cat, index) => {
    const details = (cat.questions || [])
      .filter(q => q.is_answered)
      .map(q => {
        let status = 'good';
        const answer = q.answer;
        const answerLower = typeof answer === 'string' ? answer.toLowerCase() : '';

        if (answerLower.includes('poor') || answerLower.includes('bad') || answerLower.includes('damaged') ||
            answerLower.includes('broken') || answerLower.includes('not working') || answerLower.includes('faulty')) {
          status = 'poor';
        } else if (answerLower.includes('average') || answerLower.includes('fair') || answerLower.includes('needs attention') ||
                   answerLower.includes('worn') || answerLower.includes('minor')) {
          status = 'average';
        }

        let type = 'qa';
        let media = null;

        if (q.media_url) {
          if (q.question_type === 'video' || (q.media_url && q.media_url.includes('video'))) {
            type = 'video';
          } else {
            type = 'photo';
          }
          media = {
            thumbnail: q.media_url,
            url: q.media_url,
            duration: type === 'video' ? '0:30' : undefined,
          };
        }

        // Helper to check if a string is a media URL/base64
        const isMediaString = (str) => {
          if (typeof str !== 'string') return false;
          return str.startsWith('data:image') || str.startsWith('data:video') || 
                 str.startsWith('http') || str.startsWith('media_ref:') ||
                 str.includes('firebase') || str.includes('base64');
        };

        // For photo/video: answer is the media, not useful as text
        const textAnswer = isMediaString(typeof answer === 'string' ? answer : '') ? null : (typeof answer === 'string' ? answer : (typeof answer === 'object' ? (answer?.selection || null) : null));

        return {
          item: q.question_text || 'Question',
          status,
          type,
          note: textAnswer || '',
          question: q.question_text,
          answer: textAnswer || '',
          media,
          captureInstruction: type !== 'qa' ? q.question_text : undefined,
          sub_answer_1: q.sub_answer_1 ? (typeof q.sub_answer_1 === 'object' ? (q.sub_answer_1.selection || '') : q.sub_answer_1) : undefined,
        };
      });

    const goodCount = details.filter(d => d.status === 'good').length;
    const totalAnswered = details.length;
    const autoRating = totalAnswered > 0 ? Math.round((goodCount / totalAnswered) * 10) : 0;
    
    // Rating fallback chain (matches Q&A tab logic exactly):
    // 1. Saved category_ratings  2. AI category/condition ratings  3. Auto-calculated
    const categoryKey = (cat.category_name || '').toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    const rawSavedRating = categoryRatings[categoryKey] || categoryRatings[cat.category_id];
    const savedRating = typeof rawSavedRating === 'object' && rawSavedRating !== null ? (rawSavedRating.rating ?? undefined) : rawSavedRating;
    const aiRating = getAICategoryRating(cat.category_name, categoryKey);
    const rating = savedRating ?? aiRating ?? autoRating;

    return {
      id: cat.category_id || `cat-${index}`,
      name: cat.category_name || 'General',
      icon: 'Settings',
      rating,
      checkpoints: cat.total_questions || 0,
      details,
    };
  });
}

/**
 * Transform OBD data to report format
 */
export function transformObdToReportFormat(obdScan) {
  if (!obdScan || !obdScan.completed) {
    return {
      connected: false,
      protocol: '',
      systems: [],
      dtcCodes: [],
      summary: { totalCodes: 0, criticalCount: 0, warningCount: 0, infoCount: 0 },
    };
  }

  const dtcCodes = [];
  
  // Extract codes from various possible structures:
  // 1. obdScan.dtc_codes (legacy flat array)
  // 2. obdScan.data.dtc_codes (nested under data)
  // 3. obdScan.data.categories[].codes (categorized structure from OBD scanner)
  let allCodes = obdScan.dtc_codes || [];
  
  // Check nested data structure
  const obdData = obdScan.data || {};
  if (!allCodes.length && obdData.dtc_codes) {
    allCodes = obdData.dtc_codes;
  }
  
  // Check categorized structure (e.g., {categories: [{name: "P", codes: [{code, description}]}]})
  if (!allCodes.length && obdData.categories && Array.isArray(obdData.categories)) {
    for (const category of obdData.categories) {
      if (category.codes && Array.isArray(category.codes)) {
        allCodes.push(...category.codes);
      }
    }
  }
  
  const aiAnalysis = obdScan.ai_analysis || obdData.dtc_analysis || {};
  
  // Handle both object format (code_analysis) and array format (analyzed_codes)
  let codeAnalysis = aiAnalysis.code_analysis || aiAnalysis.codes || {};
  
  // Convert analyzed_codes array to object keyed by code
  if (Array.isArray(aiAnalysis.analyzed_codes)) {
    codeAnalysis = {};
    for (const item of aiAnalysis.analyzed_codes) {
      if (item.code) {
        codeAnalysis[item.code] = item;
      }
    }
  }
  
  const reportSelection = obdScan.report_selection || { mode: 'all', selected_codes: [] };

  const selectedSet = new Set(reportSelection.selected_codes);
  const filteredCodes = reportSelection.mode === 'all' ? allCodes : allCodes.filter(c => {
    const code = typeof c === 'string' ? c : (c.code || '');
    return selectedSet.has(code);
  });

  const systemMap = {};
  
  // Component name mapping (matching Live Progress Modal exactly)
  const componentFullNames = {
    'ECM': 'Engine Control Module',
    'TCM': 'Transmission Control Module',
    'ABS': 'Anti-lock Braking System',
    'BCM': 'Body Control Module',
    'HVAC': 'Climate Control System',
    'SRS': 'Airbag/Safety System',
    'PCM': 'Powertrain Control Module',
    'EPS': 'Electric Power Steering',
    'FUEL': 'Fuel System',
    'EMISSION': 'Emissions System',
    'ELECTRICAL': 'Electrical System',
    'NETWORK': 'CAN Bus/Network',
    'UNKNOWN': 'Uncategorized'
  };
  
  // Fallback category names for DTC code prefix
  const categoryNames = {
    'P': 'Powertrain',
    'U': 'Network/Communication', 
    'B': 'Body',
    'C': 'Chassis'
  };

  for (const codeEntry of filteredCodes) {
    const code = typeof codeEntry === 'string' ? codeEntry : (codeEntry.code || codeEntry.dtc_code || '');
    const description = typeof codeEntry === 'object' ? (codeEntry.description || codeEntry.meaning || `Diagnostic code ${code}`) : `Diagnostic code ${code}`;
    const status = typeof codeEntry === 'object' ? (codeEntry.status || 'Stored') : 'Stored';

    if (!code) continue;

    const analysis = codeAnalysis[code] || {};
    let severity = 'info';
    if (analysis.severity === 'CRITICAL' || analysis.severity === 'critical') severity = 'critical';
    else if (analysis.severity === 'IMPORTANT' || analysis.severity === 'important' || analysis.severity === 'WARNING') severity = 'warning';

    const faultEntry = {
      code,
      description: analysis.description || description,
      severity,
      status,
      system: analysis.system || 'General',
      aiComponent: analysis.component || null,
      // Check multiple possible field names for causes, symptoms, and solutions
      possibleCauses: analysis?.possible_causes || analysis?.possibleCauses || codeEntry.possible_causes || codeEntry.possibleCauses || [],
      symptoms: analysis?.symptoms || codeEntry.symptoms || [],
      solutions: analysis?.recommended_solutions || analysis?.solutions || codeEntry.recommended_solutions || codeEntry.solutions || [],
      estimatedCost: analysis?.estimated_cost || null,
    };

    dtcCodes.push(faultEntry);

    // Group by AI component (ECM, TCM, EMISSION, etc.) - matches Live Progress Modal
    // Fall back to DTC code prefix if no AI component
    let systemKey = analysis.component || 'UNKNOWN';
    let systemName = componentFullNames[systemKey] || systemKey;
    
    // Fallback to DTC prefix category if no AI component
    if (systemKey === 'UNKNOWN' && code) {
      const prefix = code.charAt(0).toUpperCase();
      if (categoryNames[prefix]) {
        systemKey = prefix;
        systemName = categoryNames[prefix];
      }
    }
    
    if (!systemMap[systemKey]) {
      systemMap[systemKey] = { 
        name: systemName, 
        shortName: systemKey,
        code: systemKey,
        icon: systemKey === 'ECM' ? 'Cog' : 
              systemKey === 'TCM' ? 'Settings' : 
              systemKey === 'BCM' ? 'Shield' :
              systemKey === 'EMISSION' ? 'Cog' :
              systemKey === 'FUEL' ? 'Cog' :
              'Settings',
        status: 'normal', 
        faults: [], 
        codes: [], 
        criticalCount: 0, 
        importantCount: 0,
        nonImportantCount: 0,
        errorCount: 0
      };
    }
    systemMap[systemKey].faults.push(faultEntry);
    systemMap[systemKey].codes.push(code);
    systemMap[systemKey].errorCount++;
    if (severity === 'critical') systemMap[systemKey].criticalCount++;
    else if (severity === 'warning') systemMap[systemKey].importantCount++;
    else systemMap[systemKey].nonImportantCount++;
  }

  const systems = Object.values(systemMap)
    .map(s => ({ ...s, status: s.criticalCount > 0 ? 'critical' : s.importantCount > 0 ? 'warning' : 'normal' }))
    .sort((a, b) => (b.criticalCount * 100 + b.importantCount * 10) - (a.criticalCount * 100 + a.importantCount * 10));

  const criticalCount = dtcCodes.filter(c => c.severity === 'critical').length;
  const warningCount = dtcCodes.filter(c => c.severity === 'warning').length;

  return {
    connected: true,
    protocol: obdScan.protocol || obdData.protocol || 'OBD-II',
    systems,
    dtcCodes,
    totalErrors: dtcCodes.length,
    liveData: obdScan.live_data || obdData.live_data || {},
    readinessMonitors: obdScan.readiness_monitors || obdData.readiness_monitors || {},
    summary: { totalCodes: dtcCodes.length, criticalCount, warningCount, infoCount: dtcCodes.length - criticalCount - warningCount },
  };
}

/**
 * Transform raw inspection data into the full report data structure
 */
export function transformInspectionToReport(inspection, lead, customer, liveProgressData = {}, repairsData = {}) {
  const aiInsights = inspection.ai_insights || {};
  const conditionRatings = aiInsights.condition_ratings || {};
  const vaahanData = inspection.vaahan_data || {};
  const obdScan = liveProgressData.obd_scan || {};
  const rawCategories = liveProgressData.categories || [];
  const transformedCategories = transformCategoriesToReportFormat(rawCategories, inspection.category_ratings || {}, aiInsights);
  const transformedObd = transformObdToReportFormat(obdScan);

  // Extract accident/flood status from Q&A answers in General Information category
  // Q&A answers take priority over inspection-level fields (which are only set by service history)
  const qaAccidentFlood = (() => {
    let isAccidental = null;
    let isFloodDamaged = null;
    for (const cat of rawCategories) {
      const catName = (cat.category_name || '').toLowerCase();
      if (!catName.includes('general')) continue;
      for (const q of (cat.questions || [])) {
        if (!q.is_answered) continue;
        const qText = (q.question_text || '').toLowerCase();
        const answer = q.answer;
        const selection = (typeof answer === 'string' ? answer : (answer?.selection || '')).toLowerCase().trim();
        if (qText.includes('accident')) {
          isAccidental = selection === 'yes';
        }
        if (qText.includes('flood')) {
          isFloodDamaged = selection === 'yes';
        }
      }
    }
    return { isAccidental, isFloodDamaged };
  })();

  // Extract repairs and filter by saved selection
  const minorRepairsRaw = repairsData.minor_repairs || [];
  const majorRepairsRaw = repairsData.major_repairs || [];

  const savedSelection = inspection.repair_report_selection;
  let minorRepairs = minorRepairsRaw;
  let majorRepairs = majorRepairsRaw;

  if (savedSelection && Array.isArray(savedSelection.selected_repairs)) {
    const selectedSet = new Set(savedSelection.selected_repairs);
    minorRepairs = minorRepairsRaw.filter(r => selectedSet.has(r.part_id || r.id));
    majorRepairs = majorRepairsRaw.filter(r => selectedSet.has(r.part_id || r.id));
  }

  const totalMinor = minorRepairs.reduce((sum, r) => sum + (r.cost || 0), 0);
  const totalMajor = majorRepairs.reduce((sum, r) => sum + (r.cost || 0), 0);

  // Calculate conditions from category ratings (same logic as LiveProgressModal)
  // This ensures report shows same values as AI Analysis tab
  const calculateConditionFromRatings = (categoryRatings, groupKeywords) => {
    if (!categoryRatings || typeof categoryRatings !== 'object') return null;
    
    const ratings = [];
    Object.entries(categoryRatings).forEach(([key, value]) => {
      const keyLower = (key || '').toLowerCase();
      const rating = typeof value === 'object' && value !== null ? (value.rating ?? 0) : (value ?? 0);
      
      const matchesGroup = groupKeywords.some(keyword => keyLower.includes(keyword));
      if (matchesGroup && rating > 0) {
        ratings.push(rating);
      }
    });
    
    if (ratings.length === 0) return null;
    const avg = Math.round(ratings.reduce((a, b) => a + b, 0) / ratings.length);
    
    if (avg >= 8) return 'GOOD';
    if (avg >= 6) return 'FAIR';
    if (avg >= 4) return 'AVERAGE';
    if (avg > 0) return 'POOR';
    return 'PENDING';
  };
  
  const engineKeywords = ['engine', 'exhaust', 'oil', 'battery', 'electrical', 'obd', 'scanner', 'health', 'diagnosis'];
  const exteriorKeywords = ['exterior', 'bumper', 'fender', 'headlight', 'taillight', 'hood', 'trunk', 'windshield', 'glass', 'door panel', 'tyre', 'tire', 'wheel', 'physical'];
  const interiorKeywords = ['interior', 'infotainment', 'air condition', 'ac ', 'cooling', 'accessories'];
  const transmissionKeywords = ['transmission', 'gearbox', 'clutch', 'suspension', 'brake'];
  
  const calculatedConditions = {
    engine: calculateConditionFromRatings(inspection.category_ratings, engineKeywords),
    exterior: calculateConditionFromRatings(inspection.category_ratings, exteriorKeywords),
    interior: calculateConditionFromRatings(inspection.category_ratings, interiorKeywords),
    transmission: calculateConditionFromRatings(inspection.category_ratings, transmissionKeywords)
  };

  const getAssessmentText = () => {
    const summary = inspection.assessment_summary || aiInsights.assessment_summary;
    if (!summary) return "This vehicle has been professionally inspected. Detailed assessment available below.";
    if (typeof summary === 'string') return summary;
    if (typeof summary === 'object') {
      const parts = [];
      if (summary.overall) parts.push(summary.overall);
      if (summary.engine_and_mechanical) parts.push(`Engine & Mechanical: ${summary.engine_and_mechanical}`);
      if (summary.exterior_body) parts.push(`Exterior: ${summary.exterior_body}`);
      if (summary.interior_comfort) parts.push(`Interior: ${summary.interior_comfort}`);
      if (summary.safety_systems) parts.push(`Safety: ${summary.safety_systems}`);
      if (summary.documentation) parts.push(`Documentation: ${summary.documentation}`);
      return parts.join(' ') || "This vehicle has been professionally inspected. Detailed assessment available below.";
    }
    return "This vehicle has been professionally inspected. Detailed assessment available below.";
  };

  return {
    header: {
      reportFor: inspection.package_name || "Vehicle Inspection Report",
      customerName: inspection.customer_name || customer?.name || lead?.customer_name || "Customer",
      customerPhone: inspection.customer_mobile || customer?.phone || lead?.phone_number || "",
      vehicleNumber: inspection.car_number || lead?.vehicle_number || "",
      inspectionRequestedDate: lead?.created_at
        ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : inspection.created_at
          ? new Date(inspection.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : "-",
      inspectedOn: inspection.inspection_date
        ? new Date(inspection.inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : inspection.inspection_completed_at
          ? new Date(inspection.inspection_completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : inspection.updated_at
            ? new Date(inspection.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : "-",
      inspectedBy: inspection.mechanic_name || "WiseDrive Mechanic",
      inspectionType: inspection.package_name || "Standard Inspection",
      location: inspection.city || lead?.city || "",
      marketValue: {
        min: inspection.market_value_min || aiInsights.market_value?.min || 0,
        max: inspection.market_value_max || aiInsights.market_value?.max || 0,
        currency: "\u20B9",
        confidence: aiInsights.market_value?.confidence || "medium",
      },
      recommendedToBuy: inspection.recommended_to_buy ?? aiInsights.recommended_to_buy ?? false,
      overallRating: inspection.overall_rating || aiInsights.overall_rating || 0,
      checkpointsInspected: inspection.checkpoints_inspected || 0,
      isPublished: inspection.report_published || false,
      lastSaved: inspection.updated_at ? new Date(inspection.updated_at).toLocaleString('en-IN') : "-",
      aiGenerated: aiInsights.ai_generated || !!inspection.ai_report_generated_at,
      aiGeneratedAt: aiInsights.generated_at || inspection.ai_report_generated_at || null,
    },
    vehicleInfo: {
      make: inspection.vehicle_make || vaahanData.manufacturer || lead?.vehicle_make || "",
      model: inspection.vehicle_model || vaahanData.model || lead?.vehicle_model || "",
      year: inspection.vehicle_year || vaahanData.manufacturing_date?.split('/')[1] || lead?.vehicle_year || 0,
      mfgDate: inspection.manufacturing_date || vaahanData.manufacturing_date || inspection.mfg_date || "",
      fuel: inspection.fuel_type || vaahanData.fuel_type || lead?.fuel_type || "",
      transmission: inspection.transmission || "",
      owners: inspection.owners || parseInt(vaahanData.owner_count) || 0,
      regNo: inspection.car_number || vaahanData.registration_number || lead?.vehicle_number || "",
      colour: inspection.vehicle_colour || vaahanData.color || "",
      regDate: inspection.registration_date || vaahanData.registration_date || inspection.reg_date || "",
      engineCC: inspection.engine_cc || parseInt(vaahanData.cubic_capacity) || 0,
      engineNo: inspection.engine_number || inspection.engine_no || vaahanData.engine_number || "",
      chassisNo: inspection.chassis_number || inspection.chassis_no || vaahanData.chassis_number || "",
      bodyType: inspection.body_type || vaahanData.body_type || "",
      vehicleClass: inspection.vehicle_class || vaahanData.vehicle_class || "",
      ownerName: inspection.owner_name || vaahanData.owner_name || "",
    },
    assessmentSummary: {
      paragraph: getAssessmentText(),
      keyHighlights: inspection.key_highlights || aiInsights.key_highlights || [],
      riskFactors: aiInsights.risk_factors || [],
      recommendations: aiInsights.recommendations || [],
    },
    keyInfo: {
      kmsDriven: inspection.kms_driven || lead?.kms_driven || 0,
      // Priority: DB value > Calculated from category ratings > AI insights > PENDING
      engineCondition: (inspection.engine_condition && inspection.engine_condition !== 'PENDING') 
        ? inspection.engine_condition 
        : (calculatedConditions.engine || conditionRatings.engine || "PENDING"),
      interiorCondition: (inspection.interior_condition && inspection.interior_condition !== 'PENDING') 
        ? inspection.interior_condition 
        : (calculatedConditions.interior || conditionRatings.interior || "PENDING"),
      transmission: (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING') 
        ? inspection.transmission_condition 
        : (calculatedConditions.transmission || conditionRatings.transmission || "PENDING"),
      transmissionCondition: (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING') 
        ? inspection.transmission_condition 
        : (calculatedConditions.transmission || conditionRatings.transmission || "PENDING"),
      exteriorCondition: (inspection.exterior_condition && inspection.exterior_condition !== 'PENDING') 
        ? inspection.exterior_condition 
        : (calculatedConditions.exterior || conditionRatings.exterior || "PENDING"),
      accident: qaAccidentFlood.isAccidental !== null ? qaAccidentFlood.isAccidental : (inspection.accident_history || false),
      floodDamage: qaAccidentFlood.isFloodDamaged !== null ? qaAccidentFlood.isFloodDamaged : (inspection.flood_damage || false),
      dentsScratches: inspection.dents_scratches || false,
      insurance: (() => {
        let insuranceStatus = inspection.insurance_status;
        if (!insuranceStatus || insuranceStatus === 'Unknown') {
          const expiryDate = inspection.insurance_expiry || vaahanData.insurance_valid_upto;
          if (expiryDate) {
            const expiry = new Date(expiryDate);
            insuranceStatus = expiry > new Date() ? 'Active' : 'Expired';
          } else {
            insuranceStatus = 'Unknown';
          }
        }
        return {
          status: insuranceStatus,
          insurerName: inspection.insurer_name || vaahanData.insurance_company || "",
          policyNumber: inspection.policy_number || vaahanData.insurance_policy_number || "",
          expiryDate: inspection.insurance_expiry || vaahanData.insurance_valid_upto || "",
          policyType: inspection.policy_type || "",
          idvValue: inspection.idv_value || 0,
        };
      })(),
      tyreDetails: {
        avgLife: inspection.tyre_avg_life || 0,
        tyres: inspection.tyre_details || [],
      },
      // Only use minorRepairs/majorRepairs to avoid duplication in RepairsSection
      repairs: [],
      calculatedRepairs: [],
      minorRepairs,
      majorRepairs,
      totalMinorRepairsCost: totalMinor,
      totalMajorRepairsCost: totalMajor,
      totalRepairCostMin: inspection.total_repair_cost_min || totalMinor + totalMajor,
      totalRepairCostMax: inspection.total_repair_cost_max || Math.round((totalMinor + totalMajor) * 1.2),
    },
    rtoVerification: {
      status: inspection.rto_verification_status || (vaahanData.status ? "VERIFIED" : "PENDING"),
      challans: inspection.challan_data?.challan_details || vaahanData.challans || inspection.challans || [],
      trafficChallans: inspection.challan_data?.total_pending_amount || 0,
      totalChallans: inspection.challan_data?.total_challans || 0,
      pendingCount: inspection.challan_data?.pending_count || 0,
      hypothecation: inspection.hypothecation || vaahanData.hypothecation || null,
      blacklist: inspection.blacklist_status || vaahanData.blacklist_status || false,
      registrationAuthority: inspection.registration_authority || vaahanData.registration_authority || "",
      registrationValidity: inspection.rc_expiry_date || vaahanData.rc_expiry_date || "",
      fitnessValidity: inspection.fitness_upto || vaahanData.fitness_upto || "",
      taxValidity: inspection.tax_valid_upto || vaahanData.tax_valid_upto || "",
      insuranceValidity: inspection.insurance_expiry || vaahanData.insurance_valid_upto || "",
      puccValidity: inspection.pucc_valid_upto || vaahanData.pucc_valid_upto || "",
      noc: vaahanData.noc_status || "",
      financed: inspection.is_financed ?? vaahanData.financed ?? false,
      financier: inspection.hypothecation || vaahanData.financer || "",
      puccNumber: inspection.pucc_number || vaahanData.pucc_number || "",
    },
    obdReport: transformedObd,
    inspectionCategories: transformedCategories.length > 0 ? transformedCategories : (inspection.inspection_categories || []),
    categoryRatings: inspection.category_ratings || aiInsights.category_ratings || {},
    footer: {
      companyName: "WiseDrive",
      supportPhone: "+91 88848 54885",
      supportEmail: "support@wisedrive.in",
      website: "www.wisedrive.in",
    },
    aiInsights,
    vaahanData,
    // Add estimatedRepairs at root level for RepairsSection component
    estimatedRepairs: {
      repairs: [],
      calculatedRepairs: [],
      minorRepairs,
      majorRepairs,
      totalMinor,
      totalMajor,
    },
  };
}


/**
 * Transform a published report snapshot to the format expected by report components.
 * 
 * This is MUCH simpler than transformInspectionToReport because the snapshot
 * already contains pre-computed, pre-transformed data that was captured at publish time.
 * 
 * GUARANTEED to match what Preview showed when report was published.
 */
export function transformSnapshotToReport(snapshotResponse) {
  const {
    keyInfo = {},
    ratings = {},
    aiSummary = {},
    obdReport = {},
    estimatedRepairs = {},
    inspectionCategories = [],
    overallStats = {},
    rtoVerification = {},
    marketResearch,
    aiInsights = {},
    vaahanData = {},
  } = snapshotResponse.snapshot || snapshotResponse;

  // Transform categories using existing function
  // Pass aiInsights.category_ratings as the categoryRatings parameter
  const categoryRatings = aiInsights?.category_ratings || {};
  const transformedCategories = transformCategoriesToReportFormat(inspectionCategories, categoryRatings, aiInsights);

  // Transform OBD data from snapshot format
  const transformedObd = transformObdToReportFormat(obdReport);

  // Handle both snake_case (from API) and camelCase formats
  const minorRepairs = estimatedRepairs.minor_repairs || estimatedRepairs.minorRepairs || [];
  const majorRepairs = estimatedRepairs.major_repairs || estimatedRepairs.majorRepairs || [];
  const totalMinor = estimatedRepairs.total_minor || estimatedRepairs.totalMinor || 0;
  const totalMajor = estimatedRepairs.total_major || estimatedRepairs.totalMajor || 0;

  // Extract condition ratings from aiInsights (engine: POOR, interior: GOOD, etc.)
  const conditionRatings = aiInsights?.condition_ratings || {};
  
  // Build assessment paragraph from AI insights
  const getAssessmentParagraph = () => {
    // Priority: aiInsights.assessment_summary.overall > aiSummary.overall_summary > fallback
    if (aiInsights?.assessment_summary?.overall) {
      return aiInsights.assessment_summary.overall;
    }
    if (aiSummary?.overall_summary) {
      return aiSummary.overall_summary;
    }
    if (aiInsights?.summary) {
      return aiInsights.summary;
    }
    if (aiInsights?.ai_analysis?.summary) {
      return aiInsights.ai_analysis.summary;
    }
    return "This vehicle has been professionally inspected. Detailed assessment available below.";
  };

  // Extract vaahan/RTO data for vehicle details
  const rcDetails = rtoVerification?.rc_details || vaahanData || {};

  return {
    header: {
      reportTitle: "Vehicle Inspection Report",
      reportDate: snapshotResponse.published_at || new Date().toISOString(),
      inspectionId: snapshotResponse.inspection_id,
      reportId: snapshotResponse.report_id,
      customerName: snapshotResponse.customer_name || keyInfo.customer_name || "Customer",
      location: keyInfo.city || keyInfo.inspection_location || "",
      inspectedOn: keyInfo.inspection_date || snapshotResponse.published_at || "",
      inspectedBy: keyInfo.mechanic_name || "WiseDrive Inspector",
      inspectionType: keyInfo.package_name || "Comprehensive Inspection",
      marketValue: {
        min: marketResearch?.recommended_min || marketResearch?.market_min || marketResearch?.min_price || marketResearch?.price_min || aiInsights?.market_value?.min || 0,
        max: marketResearch?.recommended_max || marketResearch?.market_max || marketResearch?.max_price || marketResearch?.price_max || aiInsights?.market_value?.max || 0,
      },
      overallRating: ratings.overall_rating || aiInsights?.overall_rating || 0,
      isRecommended: ratings.recommended_to_buy ?? aiInsights?.recommended_to_buy ?? false,
    },
    vehicleInfo: {
      registrationNumber: keyInfo.vehicle_registration || rcDetails.registration_number || "",
      make: keyInfo.vehicle_make || rcDetails.manufacturer || "",
      model: keyInfo.vehicle_model || rcDetails.model || "",
      variant: keyInfo.vehicle_variant || "",
      year: keyInfo.vehicle_year || "",
      color: keyInfo.vehicle_color || rcDetails.color || "",
      colour: keyInfo.vehicle_color || rcDetails.color || "",
      fuel: keyInfo.fuel_type || rcDetails.fuel_type || "",
      fuelType: keyInfo.fuel_type || rcDetails.fuel_type || "",
      transmission: keyInfo.transmission || "",
      odometerReading: keyInfo.odometer || 0,
      ownerNumber: keyInfo.owner_number || rcDetails.owner_count || "",
      mfgDate: rcDetails.manufacturing_date || keyInfo.mfg_date || "",
      regNo: keyInfo.vehicle_registration || rcDetails.registration_number || "",
      regDate: rcDetails.registration_date || keyInfo.reg_date || "",
      engineCC: rcDetails.cubic_capacity || keyInfo.engine_cc || 0,
      engineNo: rcDetails.engine_number || keyInfo.engine_number || "",
      chassisNo: rcDetails.chassis_number || keyInfo.chassis_number || "",
    },
    assessmentSummary: {
      overallRating: ratings.overall_rating || aiInsights?.overall_rating || 0,
      recommendedToBuy: ratings.recommended_to_buy ?? aiInsights?.recommended_to_buy ?? false,
      // AssessmentSummary.jsx expects `paragraph` NOT `summary`
      paragraph: getAssessmentParagraph(),
      summary: getAssessmentParagraph(),
      keyHighlights: aiInsights?.key_highlights || aiSummary?.pros || [],
      pros: aiSummary?.pros || aiInsights?.key_highlights || [],
      cons: aiSummary?.cons || aiInsights?.concerns || [],
      recommendations: aiSummary?.recommendations || aiInsights?.recommendations || [],
      negotiationPoints: aiSummary?.negotiation_points || [],
      riskFactors: aiInsights?.risk_factors || [],
    },
    keyInfo: {
      overallScore: ratings.overall_rating || aiInsights?.overall_rating || 0,
      recommended: ratings.recommended_to_buy ?? aiInsights?.recommended_to_buy ?? false,
      kmsDriven: keyInfo.odometer || keyInfo.kms_driven || 0,
      // Condition ratings from AI insights (engine: POOR, interior: GOOD, etc.)
      engineCondition: conditionRatings.engine || "PENDING",
      transmissionCondition: conditionRatings.transmission || "PENDING",
      interiorCondition: conditionRatings.interior || "PENDING",
      exteriorCondition: conditionRatings.exterior || "PENDING",
      // Boolean flags
      accident: keyInfo.accident_history || false,
      floodDamage: keyInfo.flood_damage || false,
      // Insurance data
      insurance: {
        status: rtoVerification?.insurance_details?.status || keyInfo.insurance_status || "Unknown",
        insurerName: rtoVerification?.insurance_details?.insurer_name || "",
        policyNumber: rtoVerification?.insurance_details?.policy_number || "",
        expiryDate: rtoVerification?.insurance_details?.expiry_date || "",
      },
      // Tyre details
      tyreDetails: {
        avgLife: ratings.tyre_life_percentage || keyInfo.tyre_avg_life || 0,
        tyres: keyInfo.tyre_details || [],
      },
      // Ratings for category breakdown
      ratings: {
        engine: ratings.engine_rating || aiInsights?.category_ratings?.engine,
        transmission: ratings.transmission_rating || aiInsights?.category_ratings?.transmission,
        exterior: ratings.exterior_rating || aiInsights?.category_ratings?.exterior,
        interior: ratings.interior_rating || aiInsights?.category_ratings?.interior,
        suspension: ratings.suspension_rating || aiInsights?.category_ratings?.suspension,
        electrical: ratings.electrical_rating || aiInsights?.category_ratings?.electrical,
        ac: ratings.ac_rating || aiInsights?.category_ratings?.ac,
      },
      tyreLifePercentage: ratings.tyre_life_percentage || 0,
      totalQuestions: overallStats.total_questions || 0,
      answeredQuestions: overallStats.answered_questions || 0,
      completionPercentage: overallStats.completion_percentage || 0,
      // Repairs data for KeyInfoSection
      minorRepairs,
      majorRepairs,
      totalMinorRepairsCost: totalMinor,
      totalMajorRepairsCost: totalMajor,
    },
    estimatedRepairs: {
      // Don't set repairs/calculatedRepairs when minorRepairs/majorRepairs are set
      // RepairsSection combines all three, causing duplication
      repairs: [],
      calculatedRepairs: [],
      minorRepairs,
      majorRepairs,
      totalMinorRepairsCost: totalMinor,
      totalMajorRepairsCost: totalMajor,
      totalRepairCostMin: totalMinor + totalMajor,
      totalRepairCostMax: Math.round((totalMinor + totalMajor) * 1.2),
    },
    rtoVerification: {
      status: rtoVerification.rc_details ? "VERIFIED" : "PENDING",
      challans: rtoVerification.challan_data?.challan_details || [],
      trafficChallans: rtoVerification.challan_data?.total_pending_amount || 0,
      totalChallans: rtoVerification.challan_data?.total_challans || 0,
      pendingCount: rtoVerification.challan_data?.pending_count || 0,
      hypothecation: rtoVerification.hypothecation,
      blacklist: rtoVerification.blacklist_status || false,
      rcDetails: rtoVerification.rc_details || {},
      insuranceDetails: rtoVerification.insurance_details || {},
    },
    obdReport: transformedObd,
    inspectionCategories: transformedCategories,
    categoryRatings: aiInsights.category_ratings || {},
    footer: {
      companyName: "WiseDrive",
      supportPhone: "+91 88848 54885",
      supportEmail: "support@wisedrive.in",
      website: "www.wisedrive.in",
    },
    aiInsights,
    marketResearch,
  };
}
